import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Paperclip, Send } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import type { TfxDraftDTO } from '@/api/generated/model';
import { uploadDraftAsset, useSubmitTfxDraft } from '../api';
import { TfxStatusBadge } from './TfxStatusBadge';
import { TfxPreviewVideo } from './TfxPreviewVideo';
import { formatModifiedAt, packageLabel } from '../lib/display';

/**
 * One "my drafts" row (pattern: EffectPackCandidateCard). The submit mutation is instantiated
 * per-card so `isPending` disables only THIS card's button.
 */
export function TfxDraftCard({ draft }: { draft: TfxDraftDTO }) {
  const confirm = useConfirm();
  const submitMutation = useSubmitTfxDraft();
  // S2 sidecar rail: side-assets uploaded against THIS card in this session.
  // The server is the source of truth (approve cross-checks by file name);
  // the chips are the author's local receipt.
  const [uploadedAssets, setUploadedAssets] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onPickAsset = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file || id == null) return;
    setUploading(true);
    try {
      const name = await uploadDraftAsset(id, file);
      setUploadedAssets((prev) => (prev.includes(name) ? prev : [...prev, name]));
      toast.success(`Asset "${name}" attached`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to attach asset');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const id = draft.id;
  const label = packageLabel(draft.packageId, draft.version);
  const status = draft.status?.trim().toUpperCase() ?? '';
  const note = draft.note?.trim();
  const rejectReason = draft.rejectReason?.trim();

  const onSubmit = async () => {
    if (id == null) return;
    const { confirmed } = await confirm({
      title: 'Submit for review',
      description: `Submit "${label}" for review? A reviewer will approve or reject it.`,
      confirmText: 'Submit',
      variant: 'default',
    });
    if (!confirmed) return;
    try {
      await submitMutation.mutateAsync({ id });
      toast.success('Draft submitted for review');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit draft');
    }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="flex items-start gap-4">
          <TfxPreviewVideo url={draft.previewUrl} />

          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate font-mono text-sm font-semibold">{label}</p>
              <TfxStatusBadge status={status} />
              <span className="text-xs text-muted-foreground">iteration {draft.iteration ?? 0}</span>
            </div>

            {note && <p className="text-sm text-muted-foreground">{note}</p>}

            {status === 'REJECTED' && rejectReason && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                <span>{rejectReason}</span>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Modified {formatModifiedAt(draft.modifiedAt)}
            </p>
          </div>
        </div>

        {status === 'DRAFT' && (
          <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.mov"
                className="hidden"
                onChange={(e) => void onPickAsset(e.target.files)}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={uploading || id == null}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="size-4" />
                {uploading ? 'Uploading…' : 'Attach asset'}
              </Button>
              {uploadedAssets.map((name) => (
                <span
                  key={name}
                  className="truncate rounded-full bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground"
                >
                  {name}
                </span>
              ))}
            </div>
            <Button
              size="sm"
              disabled={submitMutation.isPending || id == null}
              onClick={() => void onSubmit()}
            >
              <Send className="size-4" />
              Submit for review
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
